<template>
  <component
    :is="to ? NuxtLink : 'button'"
    :to="to ? to : undefined"
    :type="type ? type : !to ? 'button' : undefined"
    :class="
      variant !== 'unstyled'
        ? [
            'group relative inline-flex h-10 items-center justify-center whitespace-nowrap rounded-full font-semibold leading-none no-underline',
            {
              'bg-transparent p-0.5': variant === 'cta',
              'px-5 text-center uppercase':
                variant === 'solid' || variant === 'outline' || variant === 'ghost',
              'mix-blend-difference invert':
                (blendMode && variant === 'solid') ||
                (blendMode && variant === 'outline') ||
                (blendMode && variant === 'ghost'),
              'bg-black text-white': variant === 'solid',
              'overflow-hidden border-2 border-black text-black': variant === 'outline',
              'bg-primary bg-opacity-0 text-primary hover:bg-opacity-20': variant === 'ghost',
              'text-primary mix-blend-difference invert': variant === 'link',
              'cursor-not-allowed': disabled || loading,
              'border-disabled text-disabled': inactive,
              'lg:h-11': size === 'md',
              'lg:h-12': size === 'lg',
              'h-14 text-xl lg:h-11 lg:text-base': size === 'xl'
            }
          ]
        : undefined
    "
    :disabled="disabled"
    @click="onClick"
  >
    <template v-if="variant === 'cta'">
      <span class="ambi !absolute left-0 top-0 block h-full w-full rounded-full"></span>
      <span
        class="pointer-events-none relative inline-flex h-full w-full items-center justify-center rounded-full bg-primary px-5 text-center uppercase text-white"
      >
        {{ label }}
        <slot />
        <Icon
          v-if="icon"
          :name="typeof icon === 'string' ? icon : icon.name"
          :size="typeof icon === 'string' ? 'sm' : icon.size"
          :color="typeof icon === 'string' ? 'white group-hover:black ' : icon.color"
        />
      </span>
    </template>
    <template v-else>
      {{ label }}
      <slot />
      <span
        v-if="variant === 'outline' && hoverEffect"
        class="pointer-events-none absolute left-[-10%] top-0 inline-flex h-full w-[120%] -skew-x-12 items-center justify-center rounded-full bg-black text-white transition-all duration-500 ease-quart-out [clip-path:polygon(50%_0%,_50%_0,_50%_100%,_50%_100%)] group-hover:[clip-path:polygon(0%_0%,100%_0%,100%_100%,0%_100%)]"
        ><span class="skew-x-12">{{ label }}</span></span
      >
      <Icon
        v-if="icon"
        :name="typeof icon === 'string' ? icon : icon.name"
        :size="typeof icon === 'string' ? 'sm' : icon.size"
        :color="typeof icon === 'string' ? 'white group-hover:black ' : icon.color"
      />
    </template>
  </component>
</template>

<script setup lang="ts">
import { NuxtLink } from '#components'
import type { RouteLocationRaw } from 'vue-router'
import type { NuxtLinkProps } from 'nuxt/app'
import type { IconSize, IconName } from '@/components/media/Icon.vue'

export type ButtonVariant = 'cta' | 'ghost' | 'outline' | 'solid' | 'link' | 'unstyled'

interface Props {
  disabled?: boolean
  inactive?: boolean
  icon?: {
    color?: string
    name: IconName
    size?: IconSize
  }
  variant?: ButtonVariant
  label?: string | null
  loading?: boolean
  to?: RouteLocationRaw | NuxtLinkProps['to']
  prevent?: boolean
  size?: 'md' | 'lg' | 'xl'
  type?: HTMLButtonElement['type'] | undefined
  hoverEffect?: boolean
  blendMode?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  to: undefined,
  disabled: false,
  inactive: false,
  variant: 'solid',
  loading: false,
  icon: undefined,
  label: null,
  prevent: false,
  size: 'md',
  type: undefined,
  hoverEffect: true,
  blendMode: true
})

const emit = defineEmits<{
  (event: 'click', e: MouseEvent): void
}>()

const onClick = (event: MouseEvent) => {
  if (props.disabled || props.loading || props.prevent) {
    event.preventDefault()
    event.stopPropagation()
  }
  emit('click', event)
}
</script>
